import React, { useEffect, useState } from 'react';

// Research principle data
const researchPrinciples = [
    {
        id: 'brainwave-frequencies',
        title: 'Brainwave Frequencies',
        summary: 'Your brain operates at different electrical frequencies depending on your mental state — from deep restoration to peak cognition.',
        institutions: ['MIT', 'Stanford', 'Netherlands Institute'],
        science: 'Your brain operates at different electrical frequencies depending on your mental state — from deep sleep to intense focus. EEG (electroencephalography) technology has mapped five distinct frequency bands: Delta (0.5–4 Hz) during deep restoration, Theta (4–8 Hz) for creativity and insight, Alpha (8–12 Hz) in relaxed awareness, Beta (12–30 Hz) for active thinking, and Gamma (30–100 Hz) during peak cognitive integration. These frequencies have been established as electrical signatures of cognitive states.',
        research: [
            { institution: 'MIT Picower Institute', finding: 'Discovered that gamma rhythms dominate upper cortical layers while alpha/beta oscillations are more prominent in deeper layers' },
            { institution: 'Stanford University', finding: 'Developed optical imaging technology to observe brain waves in real-time, discovering previously unknown types of beta waves' },
            { institution: 'Netherlands Institute for Neuroscience', finding: 'Foundational brainwave categorization research establishing standard Hz ranges' }
        ],
        application: 'Each brainwave state correlates with specific emotional, energy, and focus levels. Our engine maps the information you provide during check-in to a precise brainwave state — like "Theta A - Drowsy" or "Gamma - Flow." This precision ensures each meditation is calibrated to where you actually are.'
    },
    {
        id: 'brainwave-entrainment',
        title: 'Brainwave Entrainment',
        summary: 'Your brain naturally synchronizes to external rhythms — a phenomenon we use to guide your mental state.',
        institutions: ['Stanford', 'Northwestern', 'NIH'],
        science: 'Your brain has a natural tendency to synchronize its electrical activity to external rhythmic stimuli — a phenomenon called the Frequency Following Response (FFR). When exposed to a consistent rhythm, your brain\'s neural activity locks onto that frequency.',
        research: [
            { institution: 'Stanford Translational Auditory Research Laboratory', finding: 'Published EEG datasets demonstrating FFR to speech and music stimuli, showing time-locked neural responses' },
            { institution: 'Northwestern University', finding: 'Research on FFR confirming both subcortical and cortical contributions to the entrainment response' },
            { institution: 'NIH Systematic Reviews', finding: 'Meta-analyses documenting the brainwave entrainment hypothesis and external stimulation effects' }
        ],
        application: 'We use this response to guide your brain from its current frequency to your target state. During each practice, we gradually shift the audio frequency from your current state to your selected target state — and your brain follows.'
    },
    {
        id: 'binaural-beats',
        title: 'Binaural Beats',
        summary: 'Audio frequency differentials that influence brainwave activity through the entrainment response.',
        institutions: ['UT Dallas', 'NIH'],
        science: 'When each ear receives a slightly different audio frequency, your brain perceives the difference as a rhythmic pulse — a binaural beat. This beat can influence brainwave activity through the entrainment response. A 2019 meta-analysis of 22 studies confirmed binaural beats are effective for reducing anxiety, with effects varying by frequency and exposure duration.',
        research: [
            { institution: 'Garcia-Argibay et al. (2019)', finding: 'Meta-analysis across 22 studies showing binaural beats reduce anxiety and affect memory and attention' },
            { institution: 'University of Texas at Dallas', finding: 'NIH-funded research on noninvasive brain stimulation via binaural audio' },
            { institution: 'NIH PubMed', finding: 'Systematic reviews showing 26.3% drop in pre-surgical anxiety with binaural beats' }
        ],
        application: 'Our audio layer includes precisely calibrated binaural beat frequencies targeting your calculated destination state. The beat is the active ingredient — the music and voice are the delivery mechanism. We match the beat to your journey: faster frequencies for focus, slower for restoration.'
    },
    {
        id: 'iso-principle',
        title: 'The Iso-Principle',
        summary: 'Match your current state first, then gradually transition toward the target for sustainable change.',
        institutions: ['Stanford', 'Harvard', 'Oxford'],
        science: 'You can\'t force a state change. Trying to calm a racing mind by immediately presenting calm, slow stimuli creates friction — your nervous system resists the mismatch. The research-backed solution: first match the person\'s current state, then gradually transition toward the target. Stanford\'s emotion regulation research confirms this approach.',
        research: [
            { institution: 'Stanford Psychophysiology Lab (James Gross)', finding: 'Research establishing the "process model of emotion regulation" showing gradual shifts produce more sustainable outcomes' },
            { institution: 'Harvard University', finding: 'Studies confirming that reappraisal consistently reduces negative emotions more effectively than direct suppression' },
            { institution: 'Oxford Centre for Anxiety Disorders', finding: 'Clinical therapy protocols emphasizing paced, structured transitions when treating anxiety' }
        ],
        application: 'Every meditation follows three phases: MATCH (mirror your current state for 10-20%), SLIDE (gradually shift frequency for 30-40%), and ARRIVE (anchor in the destination state for 40-50%). We don\'t fight your current state — we acknowledge it, then guide you forward.'
    },
    {
        id: 'vagus-nerve',
        title: 'Vagus Nerve & Breathing',
        summary: 'Extended exhale breathing activates your parasympathetic nervous system for rapid calming.',
        institutions: ['Stanford', 'Harvard'],
        science: 'When you exhale longer than you inhale, you activate the vagus nerve — the main communication channel between your brain and parasympathetic nervous system. Stanford researchers found that just 5 minutes daily of "cyclic sighing" (emphasizing long exhalation) reduced anxiety, improved mood, and lowered resting breathing rate more effectively than mindfulness meditation alone.',
        research: [
            { institution: 'Stanford Medicine (2023)', finding: 'Dr. David Spiegel and Andrew Huberman\'s study in Cell Reports Medicine demonstrated cyclic sighing outperforms meditation' },
            { institution: 'Harvard/Mass General Hospital', finding: 'Research on 4-7-8 breathing technique for vagal activation and sleep quality improvement' },
            { institution: 'Stanford School of Medicine', finding: 'Identified the specific brainstem neurons linking slow breathing to sense of calm' }
        ],
        application: 'When our calculator detects high-stress states (High Beta, Cortisol Noise), we integrate extended exhale breathing patterns — 4-7-8 or similar — to trigger parasympathetic activation. The exhale is the active ingredient.'
    },
    {
        id: 'default-mode-network',
        title: 'Default Mode Network',
        summary: 'Breaking rumination loops by redirecting overactive self-referential thinking patterns.',
        institutions: ['Yale', 'Harvard'],
        science: 'The Default Mode Network (DMN) activates when you\'re not focused on the external world — during mind-wandering, self-reflection, and rumination. While useful for some cognitive functions, an overactive DMN correlates with anxiety, depression, and the feeling of being mentally "stuck." Yale research demonstrated that experienced meditators show decreased DMN activity.',
        research: [
            { institution: 'Yale University (Brewer et al.)', finding: 'Seminal study in PNAS showing experienced meditators exhibit decreased activity in main DMN nodes' },
            { institution: 'Yale School of Medicine', finding: 'Research showing meditation reduces DMN activation even compared to active cognitive tasks' },
            { institution: 'Harvard Medical School', finding: 'MBCT research demonstrating that boosting interoception helps disengage from ruminative patterns' }
        ],
        application: 'Our "Ruminative Trap" state detection specifically targets stuck DMN patterns. We redirect attention with external stimuli (Sound Focus) and give the ruminating mechanism a simple task (Breath Counting) to break the loop naturally.'
    },
    {
        id: 'gamma-waves',
        title: 'Gamma Waves',
        summary: '40 Hz activity associated with peak performance, cognitive integration, and flow states.',
        institutions: ['MIT Picower', 'Harvard'],
        science: 'Gamma waves (30-100 Hz) represent the brain\'s highest-frequency activity — associated with peak performance, conscious perception, and cognitive binding. MIT\'s Picower Institute discovered that 40 Hz sensory stimulation reduces Alzheimer\'s pathology markers, prevents neuron death, and sustains memory in both mouse models and human clinical trials.',
        research: [
            { institution: 'MIT Picower Institute (Li-Huei Tsai)', finding: 'Nature, Neuron, and PLOS One publications demonstrating 40 Hz stimulation reduces amyloid and tau protein buildup' },
            { institution: 'Harvard Medical School', finding: 'Research on 40 Hz transcranial stimulation significantly reducing tau burden in human volunteers' },
            { institution: 'MIT Neuroscience (Earl K. Miller)', finding: 'Research showing gamma waves process sensory information while slower waves convey goals and rules' }
        ],
        application: 'Gamma is only accessible when all systems align: Positive emotion + High energy + Sharp focus. Our Coherence Cap prevents scattered minds from attempting gamma — you can\'t force peak cognitive integration. When conditions align, we support the flow state with minimal intervention.'
    },
    {
        id: 'interoception',
        title: 'Interoception & Body Scan',
        summary: 'Body awareness training that produces measurable structural changes in the brain.',
        institutions: ['Harvard', 'UCLA'],
        science: 'Interoception — your ability to sense internal bodily states — is a fundamental mechanism through which mindfulness works. Harvard MRI studies showed that 8 weeks of mindfulness practice increases gray matter density in regions associated with self-awareness while decreasing gray matter in the amygdala. UCLA research found that long-term meditators exhibit increased cortical folding.',
        research: [
            { institution: 'Harvard/Mass General Hospital (Sara Lazar)', finding: 'MRI studies documenting measurable brain changes from 8-week mindfulness programs' },
            { institution: 'UCLA Laboratory of Neuro Imaging', finding: 'Research showing meditators have increased gyrification enabling faster information processing' },
            { institution: 'NIH Meta-Analysis', finding: 'Evidence that mindfulness interventions lead to adaptive changes in self-reported interoception' }
        ],
        application: 'Body Scan is our tool for shifting from mental to somatic processing. Sequential attention to body regions activates interoceptive networks. We deploy this when transitioning from cognitive states (Beta) toward grounded presence (Alpha B).'
    },
    {
        id: 'sensorimotor-rhythm',
        title: 'Sensorimotor Rhythm',
        summary: 'The 12-15 Hz "zone of neutrality" — relaxed focus without activation or drowsiness.',
        institutions: ['University of Graz', 'NIH'],
        science: 'The Sensorimotor Rhythm (12-15 Hz) represents a "zone of neutrality" — relaxed focus, alert but still. SMR neurofeedback training has shown effectiveness for reducing ADHD symptoms, improving working memory in older adults, and decreasing anxiety. Research suggests SMR enhancement facilitates thalamic inhibitory mechanisms.',
        research: [
            { institution: 'University of Graz (Austria)', finding: 'Studies on SMR coherence effects on EEG patterns and working memory enhancement' },
            { institution: 'NIH PubMed', finding: 'Research showing SMR neurofeedback reduces inattentive and hyperactive symptoms in ADHD' },
            { institution: 'Frontiers in Neuroscience', finding: 'Systematic review documenting SMR\'s role in improving attention and reducing anxiety' }
        ],
        application: 'SMR is our gold standard for sustainable productivity — not excited, not tired, simply efficient. When you\'re already in SMR, we use minimal intervention: Passive Observation, gentle Box Breathing, and long silences. The goal is to sustain, not steer.'
    }
];

// Modal component
const ResearchModal: React.FC<{
    principle: typeof researchPrinciples[0] | null;
    onClose: () => void;
}> = ({ principle, onClose }) => {
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (principle) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [principle, onClose]);

    if (!principle) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

            {/* Modal content */}
            <div
                className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto bg-canvas border border-white/20 rounded-2xl p-6 md:p-10"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
                    aria-label="Close modal"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>

                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-wrap gap-3 mb-4">
                        {principle.institutions.map((inst) => (
                            <span key={inst} className="text-sm text-text-secondary">
                                {inst}
                            </span>
                        ))}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-normal tracking-[-0.02em] text-text-primary leading-[1.1]">
                        {principle.title}
                    </h2>
                </div>

                {/* The Science */}
                <div className="mb-8">
                    <h3 className="text-sm uppercase tracking-wider text-text-secondary mb-3">The Science</h3>
                    <p className="text-lg text-text-primary leading-relaxed">
                        {principle.science}
                    </p>
                </div>

                {/* The Research */}
                <div className="mb-8">
                    <h3 className="text-sm uppercase tracking-wider text-text-secondary mb-3">The Research</h3>
                    <div className="space-y-6">
                        {principle.research.map((r, i) => (
                            <div key={i}>
                                <p className="text-base font-medium text-text-primary">{r.institution}</p>
                                <p className="text-base text-text-secondary mt-1">{r.finding}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* How Nura Uses This */}
                <div>
                    <h3 className="text-sm uppercase tracking-wider text-text-secondary mb-3">How Nura Uses This</h3>
                    <p className="text-lg text-text-primary leading-relaxed">
                        {principle.application}
                    </p>
                </div>
            </div>
        </div>
    );
};

// Card component
const ResearchCard: React.FC<{
    principle: typeof researchPrinciples[0];
    onClick: () => void;
}> = ({ principle, onClick }) => {
    return (
        <button
            onClick={onClick}
            className="group text-left w-full border border-white/20 rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-white/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-text-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
        >
            {/* Institution tags */}
            <div className="flex flex-wrap gap-2 mb-4">
                {principle.institutions.slice(0, 2).map((inst) => (
                    <span key={inst} className="text-xs text-text-secondary">
                        {inst}
                    </span>
                ))}
                {principle.institutions.length > 2 && (
                    <span className="text-xs text-text-secondary">
                        +{principle.institutions.length - 2}
                    </span>
                )}
            </div>

            {/* Title */}
            <h3 className="text-xl md:text-2xl font-normal tracking-[-0.01em] text-text-primary mb-3 group-hover:text-white transition-colors">
                {principle.title}
            </h3>

            {/* Summary */}
            <p className="text-sm md:text-base text-text-secondary leading-relaxed line-clamp-3">
                {principle.summary}
            </p>

            {/* Learn more indicator */}
            <div className="mt-4 flex items-center text-sm text-text-secondary group-hover:text-text-primary transition-colors">
                <span>Learn more</span>
                <svg className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </div>
        </button>
    );
};

const ResearchPage: React.FC = () => {
    const [selectedPrinciple, setSelectedPrinciple] = useState<typeof researchPrinciples[0] | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main className="w-full bg-canvas text-text-primary">
            {/* Hero Section */}
            <section id="hero" className="relative w-full h-[70vh] flex flex-col justify-center items-center px-6 md:px-16 lg:px-24 bg-black text-white">
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-normal tracking-[-0.02em] text-white leading-[1.1] max-w-5xl text-center">
                    The science behind Nura
                </h1>
                <p className="mt-6 md:mt-8 text-lg md:text-xl text-text-primary max-w-2xl text-center">
                    Built on decades of peer-reviewed neuroscience research from the world's leading institutions.
                </p>
            </section>

            {/* Research Principles Grid */}
            <section className="relative w-full bg-canvas py-24 md:py-32">
                <div className="w-full px-6 md:px-16 lg:px-24">
                    {/* Section header */}
                    <div className="mb-12 md:mb-16">
                        <h2 className="text-5xl md:text-7xl lg:text-8xl font-normal tracking-[-0.02em] text-text-primary mb-6 md:mb-8 leading-[1.1]">
                            9 Research-Backed Principles
                        </h2>
                        <p className="text-lg md:text-xl text-text-primary max-w-2xl">
                            We don't invent new science. We synthesize decades of research into a practical system you can use every day.
                        </p>
                    </div>

                    {/* Cards grid - single column on mobile, grid on desktop */}
                    <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {researchPrinciples.map((principle) => (
                            <ResearchCard
                                key={principle.id}
                                principle={principle}
                                onClick={() => setSelectedPrinciple(principle)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Modal */}
            <ResearchModal
                principle={selectedPrinciple}
                onClose={() => setSelectedPrinciple(null)}
            />
        </main>
    );
};

export default ResearchPage;
